import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import useMessageMutation from "@/hooks/useMessageMutation";
import { MESSAGE_TAG, type PostCreateMessageBodySchema } from "@/schemas";
import { flip, offset, shift, useFloating } from "@floating-ui/react";
import ErrorIcon from "@mui/icons-material/Error";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import StrikethroughSOutlinedIcon from "@mui/icons-material/StrikethroughSOutlined";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "@tanstack/react-router";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { useMessageContext } from "../context";

interface ContextMenuProps {
	profileId: number;
	position: Record<"x" | "y", number> | null;
	onClose: () => void;
}

const ContextMenu = (props: ContextMenuProps) => {
	const { profileId, position, onClose } = props;
	const [, onCopyClipboard] = useCopyToClipboard();
	const navigate = useNavigate();
	const { deleteMessage, update } = useMessageMutation();
	const isViewOnly = useMessageContext((s) => s.isViewOnly);
	const isReadonly = useMessageContext((s) => s.isReadonly);
	const channelName = useMessageContext((s) => s.channelName);
	const moduleType = useMessageContext((s) => s.module);
	const message = useMessageContext((s) => s.message);
	const search = useMessageContext((s) => s.search);
	const { refs, strategy, x, y } = useFloating({
		placement: "top-start",
		middleware: [
			offset(10),
			flip({
				fallbackAxisSideDirection: "start",
			}),
			shift(),
		],
	});

	const { setValue } = useFormContext<PostCreateMessageBodySchema>();

	const isAiMessage = useMemo(
		() => search?.tab === MESSAGE_TAG.AI,
		[search?.tab],
	);

	if (!position || !message || !channelName) return null;

	const isMe = profileId === message.user?.id;
	const canCopy = !isEmpty(message.text);
	const canAlert = !isReadonly && message.document_count <= 0 && !isAiMessage;
	const showDelete =
		!isViewOnly &&
		!isReadonly &&
		isMe &&
		moduleType !== "cases" &&
		!isAiMessage;
	const showStrike =
		!isViewOnly && !isReadonly && !message.strike && isMe && !isAiMessage;

	const onClickStrike = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return update.mutate(
			{
				id: message.id_db,
				channelName,
				strike: !message.strike,
			},
			{
				onSuccess: () => {
					onClose();
				},
			},
		);
	};

	const onClickDelete = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return deleteMessage.mutate(
			{
				id: message.id_db,
				channelName: message.channel_name,
			},
			{
				onSuccess: () => {
					onClose();
				},
			},
		);
	};

	const onClickCopy = (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		const text = message.text;
		if (!text) return;
		return onCopyClipboard(text)
			.then(() => {
				toast.success("Copied!");
			})
			.catch(() => {
				toast.error("Failed to copied!");
			});
	};

	const onClickReply = async (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		await navigate({
			replace: true,
			// @ts-expect-error
			search: (oldSearch) => ({
				...oldSearch,
				tab: "all",
			}),
		});
		await Promise.all([
			setValue("tags", undefined),
			setValue("parent_id", message.id_db),
			setValue("message", message),
		]);
		return onClose();
	};

	const onClickAlert = async (e: React.MouseEvent<HTMLLIElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/inbox/reminders/create",
			search: {
				message: message.id_db,
			},
		}).then(() => {
			onClose();
		});
	};

	return (
		<Menu
			ref={refs.setFloating}
			open={position !== null}
			onClose={onClose}
			anchorReference="anchorPosition"
			anchorPosition={
				position !== null ? { top: position.y, left: position.x } : undefined
			}
			style={{
				position: strategy,
				top: y ?? 0,
				left: x ?? 0,
			}}
			classes={{
				paper: "w-60 !rounded-lg",
			}}
		>
			<MenuItem
				onClick={onClickReply}
				className={
					isAiMessage || isReadonly || isViewOnly ? "!hidden" : undefined
				}
			>
				<ListItemIcon>
					<ReplyOutlinedIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>Reply</ListItemText>
			</MenuItem>
			{!isAiMessage ? (
				<Divider
					className={
						!canCopy || isReadonly || isViewOnly ? "!hidden" : undefined
					}
				/>
			) : null}
			<MenuItem
				onClick={onClickCopy}
				className={!canCopy ? "!hidden" : undefined}
			>
				<ListItemIcon>
					<FileCopyOutlinedIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>Copy</ListItemText>
			</MenuItem>
			<Divider className={!showStrike ? "!hidden" : undefined} />
			<MenuItem
				className={!showStrike ? "!hidden" : undefined}
				onClick={onClickStrike}
			>
				<ListItemIcon>
					<StrikethroughSOutlinedIcon
						fontSize="small"
						className="text-zinc-950"
					/>
				</ListItemIcon>
				<ListItemText>Strikeover</ListItemText>
			</MenuItem>
			<Divider className={!showDelete ? "!hidden" : undefined} />
			<MenuItem
				className={!showDelete ? "!hidden" : undefined}
				onClick={onClickDelete}
			>
				<ListItemIcon>
					<RemoveCircleIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>Delete</ListItemText>
			</MenuItem>
			<Divider className={!canAlert ? "!hidden" : undefined} />
			<MenuItem
				className={!canAlert ? "!hidden" : undefined}
				onClick={onClickAlert}
			>
				<ListItemIcon>
					<ErrorIcon fontSize="small" className="text-zinc-950" />
				</ListItemIcon>
				<ListItemText>Alert Me</ListItemText>
			</MenuItem>
		</Menu>
	);
};

export default ContextMenu;
